import { useState } from "react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Overline, SectionHeading } from "../components/Reveal";
import { AbstractVisual, ParticleField } from "../components/AbstractVisual";
import { api, WHATSAPP_URL } from "../lib/api";

const BENEFITS = [
  "Local market expertise", "Venue partnerships", "Operations", "Marketing", "Ticketing",
  "Technology deployment", "Equipment management", "Licensing support", "Localization",
  "Customer support", "Market expansion",
];

const FIELDS = [
  ["company", "Company", "text", true], ["country", "Country", "text", true],
  ["contact_person", "Contact Person", "text", true], ["email", "Email", "email", true],
  ["website", "Website", "url", false], ["experience_name", "Experience Name", "text", true],
  ["category", "Experience Category", "text", false], ["min_space", "Minimum Space Requirement", "text", false],
  ["hardware", "Hardware Requirements", "text", false], ["concurrent_users", "Number of Concurrent Users", "text", false],
  ["existing_markets", "Existing Markets", "text", false], ["trailer_url", "Trailer URL", "url", false],
  ["pitch_deck_url", "Pitch Deck URL (Drive / Dropbox link)", "url", false],
];

export default function Creators() {
  const [form, setForm] = useState({ partnership_model: "Revenue Share" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.partner(form);
      setDone(true);
      toast.success("Partnership enquiry received. Our team will reach out shortly.");
    } catch {
      toast.error("Submission failed. Please check required fields and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main>
      <Seo title="For XR Creators" path="/creators"
        description="Take your XR experience to new markets. Vrova distributes and operates exceptional immersive content across India and emerging markets." />
      <section className="relative min-h-[75vh] flex items-end overflow-hidden">
        <AbstractVisual hue={222} seed={55} />
        <ParticleField hue={222} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/40 to-[#030305]/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-24 w-full">
          <Overline className="mb-6">For XR Creators</Overline>
          <h1 className="font-serif tracking-tight leading-[1.0] text-5xl md:text-7xl lg:text-8xl" data-testid="creators-headline">
            <MaskedLine delay={0.25}>Take Your XR Experience</MaskedLine>
            <MaskedLine delay={0.4}><span className="italic text-zinc-400">to New Markets.</span></MaskedLine>
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 font-light leading-relaxed">
            We partner with VR/XR studios, immersive storytellers and experience creators worldwide to distribute and operate exceptional immersive content across India and other emerging markets.
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeading overline="What We Handle" title={<>You Built the World. <span className="italic text-zinc-400">We Open the Doors.</span></>} />
          <div className="flex flex-wrap gap-3 mt-14">
            {BENEFITS.map((b, i) => (
              <Reveal key={b} delay={i * 0.03}>
                <span className="inline-block px-6 py-3.5 rounded-full bg-white/[0.03] border border-white/10 text-sm text-zinc-300">{b}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-36 border-t border-white/[0.06]" data-testid="creator-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionHeading overline="Partnership Enquiry" title="Become a Content Partner" />
          {done ? (
            <Reveal className="mt-14 rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="creator-form-success">
              <h3 className="font-serif text-3xl mb-4">Enquiry received.</h3>
              <p className="text-zinc-400 font-light mb-8">Thank you — our partnerships team will review your experience and respond shortly. Want to talk right now?</p>
              <a href={WHATSAPP_URL(`Hi Vrova, I just submitted a content partnership enquiry for "${form.experience_name || "my XR experience"}".`)} target="_blank" rel="noreferrer"
                data-testid="creator-success-whatsapp"
                className="inline-flex px-8 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors">
                Continue on WhatsApp
              </a>
            </Reveal>
          ) : (
            <form onSubmit={submit} data-testid="creator-partnership-form" className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
              {FIELDS.map(([key, label, type, required]) => (
                <div key={key}>
                  <label htmlFor={`creator-${key}`}>{label}{required ? " *" : ""}</label>
                  <input id={`creator-${key}`} data-testid={`creator-field-${key}`} type={type} required={required}
                    value={form[key] || ""} onChange={set(key)} />
                </div>
              ))}
              <div>
                <label htmlFor="creator-model">Partnership Model Preferred</label>
                <select id="creator-model" data-testid="creator-field-model" value={form.partnership_model} onChange={set("partnership_model")}>
                  {["Revenue Share", "Licensing", "Hybrid", "Not Sure Yet"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="creator-comments">Additional Comments</label>
                <textarea id="creator-comments" data-testid="creator-field-comments" rows={4} value={form.comments || ""} onChange={set("comments")} />
              </div>
              <div className="md:col-span-2">
                <button type="submit" data-testid="creator-form-submit" disabled={busy}
                  className="w-full md:w-auto px-10 py-4 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50">
                  {busy ? "Submitting…" : "Submit Partnership Enquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
